import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Building2 } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { supabase } from "@/shared/lib/supabase";
import { useQuery } from "@tanstack/react-query";
import { ADMIN_ROUTES } from "@/shared/constants/paths";

interface LawyerResult {
  id: number;
  user_id: number;
  full_name: string;
  email: string;
  firm_name: string | null;
  bar_number: string | null;
}

// Type for user data from join query
interface UserJoinData {
  user_id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
}

// Type for raw lawyer data from Supabase query
interface LawyerQueryResult {
  id: number;
  user_id: number;
  firm_name: string | null;
  bar_number: string | null;
  user: UserJoinData[] | null;
}

export default function LawyerSearch() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search API
  const { data: results, isLoading } = useQuery({
    queryKey: ["admin-lawyer-search", debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery.trim()) return [];

      const search = `%${debouncedQuery.trim()}%`;

      // First get matching user IDs from users table
      const { data: matchingUsers } = await supabase
        .from("users")
        .select("user_id")
        .or(`email.ilike.${search},full_name.ilike.${search}`);
      const matchingUserIds = matchingUsers?.map((u) => u.user_id) || [];

      // Build query with server-side filtering
      let query = supabase.from("lawyers").select(
        `
          id,
          user_id,
          firm_name,
          bar_number,
          user:users(user_id, email, first_name, last_name, full_name)
        `,
      );

      // Apply server-side search filter
      const lawyerFieldsFilter = `firm_name.ilike.${search},bar_number.ilike.${search}`;
      if (matchingUserIds.length > 0) {
        query = query.or(`${lawyerFieldsFilter},user_id.in.(${matchingUserIds.join(",")})`);
      } else {
        query = query.or(lawyerFieldsFilter);
      }

      query = query.limit(10);

      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map((lawyer: LawyerQueryResult) => {
        const user = lawyer.user?.[0];
        return {
          id: lawyer.id,
          user_id: lawyer.user_id,
          full_name:
            user?.full_name ||
            `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
            "Unknown",
          email: user?.email || "",
          firm_name: lawyer.firm_name,
          bar_number: lawyer.bar_number,
        };
      }) as LawyerResult[];
    },
    enabled: debouncedQuery.trim().length >= 2,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Open dropdown when there's a query
  useEffect(() => {
    if (debouncedQuery.trim().length >= 2) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [debouncedQuery]);

  const handleSelectResult = (lawyerId: number) => {
    navigate(ADMIN_ROUTES.LAWYER_DETAIL.replace(":lawyerId", String(lawyerId)));
    setSearchQuery("");
    setIsOpen(false);
  };

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative">
        <Input
          type="search"
          placeholder="Search lawyers by name, email, firm, or bar number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-4"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Searching...
            </div>
          ) : results && results.length > 0 ? (
            <div className="py-2">
              {results.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleSelectResult(result.id)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-900">{result.full_name}</div>
                  <div className="text-sm text-gray-500">{result.email}</div>
                  {result.firm_name && (
                    <div className="text-sm text-gray-400 mt-1 flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {result.firm_name}
                      {result.bar_number && ` · Bar #${result.bar_number}`}
                    </div>
                  )}
                </button>
              ))}
            </div>
          ) : debouncedQuery.trim().length >= 2 ? (
            <div className="p-4 text-center text-gray-500">No lawyers found</div>
          ) : null}
        </div>
      )}
    </div>
  );
}
